import {
  Button,
  HStack,
  Icon,
  Input,
  MenuContent,
  MenuItem,
  MenuPositioner,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  Text,
} from '@chakra-ui/react'
import { FiCheck } from 'react-icons/fi'
import { LuArchive, LuInbox, LuLayers, LuListStart } from 'react-icons/lu'
import type { OrderStatus } from '@/common/order.common'
import { UnionOrderOverviewFilterPopover } from './UnionOrderOverviewFilterPopover'

export type OrderOverviewSortBy = 'date' | 'sibLastName' | 'sibFirstName' | 'status'
export type OrderOverviewSortDir = 'asc' | 'desc'
export type OrderOverviewScope = 'OPEN' | 'ALL' | 'ARCHIVE'

const SCOPES: { key: OrderOverviewScope; label: string; icon: typeof LuInbox }[] = [
  { key: 'OPEN', label: 'Offen', icon: LuInbox },
  { key: 'ALL', label: 'Alle', icon: LuLayers },
  { key: 'ARCHIVE', label: 'Archiv', icon: LuArchive },
]

type Props = {
  searchTerm: string
  onSearchChange: (v: string) => void
  scope: OrderOverviewScope | null
  onScopeChange: (scope: OrderOverviewScope) => void
  sortBy: OrderOverviewSortBy
  sortDir: OrderOverviewSortDir
  onSortChange: (sortBy: OrderOverviewSortBy, sortDir: OrderOverviewSortDir) => void
  orderYear: number | null
  orderYearOptions: number[]
  onOrderYearChange: (orderYear: number | null) => void
  year: number | null
  availableYears: number[]
  onYearChange: (year: number | null) => void
  month: number | null
  onMonthChange: (month: number | null) => void
  activeStatuses: OrderStatus[]
  onApplyStatuses: (statuses: OrderStatus[]) => void
}

export function UnionOrderOverviewFilters({
  searchTerm,
  onSearchChange,
  scope,
  onScopeChange,
  sortBy,
  sortDir,
  onSortChange,
  orderYear,
  orderYearOptions,
  onOrderYearChange,
  year,
  availableYears,
  onYearChange,
  month,
  onMonthChange,
  activeStatuses,
  onApplyStatuses,
}: Props) {
  return (
    <HStack w="full" gap={3} align="center" flexWrap="wrap">
      <Input
        flex="1"
        minW="240px"
        placeholder="Offene Bestellungen durchsuchen, Name, Bank oder BLZ eingeben"
        value={searchTerm}
        onChange={e => onSearchChange(e.target.value)}
      />

      <HStack gap={1} borderWidth="1px" borderColor="border" borderRadius="lg" p="1">
        {SCOPES.map(s => {
          const active = scope === s.key
          return (
            <Button
              key={s.key}
              size="sm"
              variant={active ? 'subtle' : 'ghost'}
              colorPalette={active ? 'blue' : 'gray'}
              onClick={() => onScopeChange(s.key)}
            >
              <HStack gap={2}>
                <Icon as={s.icon} />
                <Text>{s.label}</Text>
              </HStack>
            </Button>
          )
        })}
      </HStack>

      <MenuRoot positioning={{ placement: 'bottom-end', gutter: 8 }}>
        <MenuTrigger asChild>
          <Button variant="subtle" size="sm">
            <HStack gap={2}>
              <LuListStart />
              <Text>Sortierung</Text>
            </HStack>
          </Button>
        </MenuTrigger>

        <MenuPositioner>
          <MenuContent minW="220px">
            <MenuItem value="sort:date" onClick={() => onSortChange('date', sortDir)}>
              <HStack w="full" justify="space-between">
                <Text>Bestelldatum</Text>
                {sortBy === 'date' ? <Icon as={FiCheck} /> : null}
              </HStack>
            </MenuItem>
            <MenuItem
              value="sort:sibLastName"
              onClick={() => onSortChange('sibLastName', sortDir)}
            >
              <HStack w="full" justify="space-between">
                <Text>SIB Nachname</Text>
                {sortBy === 'sibLastName' ? <Icon as={FiCheck} /> : null}
              </HStack>
            </MenuItem>
            <MenuItem
              value="sort:sibFirstName"
              onClick={() => onSortChange('sibFirstName', sortDir)}
            >
              <HStack w="full" justify="space-between">
                <Text>SIB Vorname</Text>
                {sortBy === 'sibFirstName' ? <Icon as={FiCheck} /> : null}
              </HStack>
            </MenuItem>
            <MenuItem value="sort:status" onClick={() => onSortChange('status', sortDir)}>
              <HStack w="full" justify="space-between">
                <Text>Status</Text>
                {sortBy === 'status' ? <Icon as={FiCheck} /> : null}
              </HStack>
            </MenuItem>

            <MenuSeparator />

            <MenuItem value="dir:desc" onClick={() => onSortChange(sortBy, 'desc')}>
              <HStack w="full" justify="space-between">
                <Text>Absteigend</Text>
                {sortDir === 'desc' ? <Icon as={FiCheck} /> : null}
              </HStack>
            </MenuItem>
            <MenuItem value="dir:asc" onClick={() => onSortChange(sortBy, 'asc')}>
              <HStack w="full" justify="space-between">
                <Text>Aufsteigend</Text>
                {sortDir === 'asc' ? <Icon as={FiCheck} /> : null}
              </HStack>
            </MenuItem>
          </MenuContent>
        </MenuPositioner>
      </MenuRoot>

      <UnionOrderOverviewFilterPopover
        orderYear={orderYear}
        orderYearOptions={orderYearOptions}
        onOrderYearChange={onOrderYearChange}
        year={year}
        availableYears={availableYears}
        onYearChange={onYearChange}
        month={month}
        onMonthChange={onMonthChange}
        activeStatuses={activeStatuses}
        onStatusesChange={onApplyStatuses}
      />
    </HStack>
  )
}
